import { createAsyncThunk } from '@reduxjs/toolkit';
import InfluencerRequestService from '../../../api/services/InfluencerRequestService';
import { base64ToFile } from '../../../utils/util';
import { MEDIA_ASSET_SALE_TAG } from '../../../utils/constants';
import axios from 'axios';

export const createInfluencerRequestPassword = createAsyncThunk(
  'influencerRequest/createInfluencerRequestPassword',
  async requestData => {
    return await InfluencerRequestService.createInfluencerRequestPassword(requestData);
  },
);

export const updateBasicInfo = createAsyncThunk('influencerRequest/updateBasicInfo', async requestData => {
  const { name, dateOfBirth, instagramUsername, twitterUsername, invitationCode } = requestData;
  const utcDateOfBirth = new Date(
    Date.UTC(dateOfBirth.getFullYear(), dateOfBirth.getMonth(), dateOfBirth.getDate()),
  ).toISOString();

  const body = {
    name,
    dateOfBirth: utcDateOfBirth,
    instagramUsername,
    ...(twitterUsername && { twitterUsername }),
    ...(invitationCode && { invitationCode }),
  };

  return await InfluencerRequestService.updateBasicInfo(body);
});

export const updateTelegramInfo = createAsyncThunk('influencerRequest/updateTelegramInfo', async requestData => {
  const { name, username, profilePhoto } = requestData;

  const formData = new FormData();

  const profilePhotoFile = base64ToFile(profilePhoto, 'profile-photo');
  formData.append('file', profilePhotoFile, profilePhotoFile.name);
  formData.append('name', name);
  formData.append('username', username);
  return await InfluencerRequestService.updateTelegramInfo(formData);
});

export const uploadSelfPhoto = createAsyncThunk('influencerRequest/uploadSelfPhoto', async requestData => {
  const { onUploadProgress, photo, type } = requestData;
  const formData = new FormData();
  formData.append('file', photo.file, photo.file.name);
  formData.append('caption', photo.caption);
  formData.append('aiDescription', photo.description);
  formData.append('tag', type);
  if (type === MEDIA_ASSET_SALE_TAG.SINGLE_PAID) {
    formData.append('cost', photo.cost);
    formData.append('category', photo.category.value);
  }
  return await InfluencerRequestService.uploadSelfPhoto(formData, onUploadProgress);
});

export const uploadAssistedPhoto = createAsyncThunk('influencerRequest/uploadAssistedPhoto', async requestData => {
  const { onProgress, photo, type } = requestData;
  const body = {
    contentType: photo.file.type,
    fileName: photo.file.name,
    tag: type,
  };
  const { url, filename } = await InfluencerRequestService.startUploadAssistedPhoto(body);

  await axios.put(url, photo.file, {
    headers: { 'Content-Type': photo.file.type },
    onUploadProgress: progressEvent => {
      const percentCompleted = (progressEvent.loaded / photo.file.size) * 100;
      onProgress(percentCompleted);
    },
  });

  const completeBody = { fileName: filename, tag: type };
  return await InfluencerRequestService.completeUploadAssistedPhoto(completeBody);
});

export const uploadSelfVideo = createAsyncThunk('influencerRequest/uploadSelfVideo', async requestData => {
  const { onUploadProgress, video, type } = requestData;
  const formData = new FormData();
  formData.append('file', video.file, video.file.name);
  formData.append('caption', video.caption);
  formData.append('aiDescription', video.description);
  formData.append('tag', type);
  if (type === MEDIA_ASSET_SALE_TAG.SINGLE_PAID) {
    formData.append('cost', video.cost);
    formData.append('category', video.category.value);
  }
  return await InfluencerRequestService.uploadSelfVideo(formData, onUploadProgress);
});

export const uploadAssistedVideo = createAsyncThunk('influencerRequest/uploadAssistedVideo', async requestData => {
  const { video, type, onProgress } = requestData;
  const body = {
    contentType: video.file.type,
    fileName: video.file.name,
    tag: type,
  };
  const { uploadId, filename } = await InfluencerRequestService.startUploadAssistedVideo(body);

  const fileSize = video.file.size;
  const partSize = 15 * 1024 * 1024; // 15MB
  const numParts = Math.ceil(fileSize / partSize);

  let uploadedBytes = 0;

  const etags = [];

  for (let i = 0; i < numParts; i++) {
    const start = i * partSize;
    const end = Math.min(start + partSize, fileSize);
    const blob = video.file.slice(start, end);
    const partNumber = i + 1;

    const uploadedSoFar = uploadedBytes;

    const getUrlBody = {
      partNumber: partNumber,
      fileName: filename,
      uploadId,
    };
    const { url } = await InfluencerRequestService.getUploadAssistedVideoUrl(getUrlBody);

    const response = await axios.put(url, blob, {
      headers: { 'Content-Type': video.file.type },
      onUploadProgress: progressEvent => {
        const totalUploaded = uploadedSoFar + progressEvent.loaded;
        const percentCompleted = (totalUploaded / video.file.size) * 100;
        onProgress(percentCompleted);
      },
    });
    const etag = response.headers.etag?.replace(/"/g, '');
    etags.push({ ETag: etag, PartNumber: partNumber });
    uploadedBytes += blob.size;
  }
  const completeBody = { fileName: filename, uploadId, parts: etags, tag: type };
  await InfluencerRequestService.completeUploadAssistedVideo(completeBody);
});

export const uploadBundle = createAsyncThunk('influencerRequest/uploadBundle', async requestData => {
  const { onUploadProgress, description, caption, cost, media, name } = requestData;
  const formData = new FormData();

  media.forEach(file => {
    formData.append('media', file.file, file.file.name);
  });

  formData.append('name', name);
  formData.append('caption', caption);
  formData.append('aiDescription', description);
  formData.append('cost', cost);

  return await InfluencerRequestService.uploadBundle(formData, onUploadProgress);
});

export const saveGallery = createAsyncThunk('influencerRequest/saveGallery', async () => {
  return await InfluencerRequestService.saveGallery();
});

export const testVoice = createAsyncThunk('influencerRequest/testVoice', async requestData => {
  const { voiceId, message, voiceSpeed } = requestData;
  return await InfluencerRequestService.testVoice(voiceId, message, voiceSpeed);
});

export const saveVoice = createAsyncThunk('influencerRequest/saveVoice', async requestData => {
  const { voiceId, voiceSpeed } = requestData;
  return await InfluencerRequestService.saveVoice(voiceId, voiceSpeed);
});

export const uploadAudio = createAsyncThunk('influencerRequest/uploadAudio', async requestData => {
  const { audio } = requestData;
  const formData = new FormData();
  formData.append('file', audio.file, audio.file.name);
  return await InfluencerRequestService.uploadAudio(formData);
});

export const uploadHighQualityAudio = createAsyncThunk(
  'influencerRequest/uploadHighQualityAudio',
  async requestData => {
    const { audio } = requestData;
    const formData = new FormData();
    formData.append('file', audio.file, audio.file.name);
    return await InfluencerRequestService.uploadHighQualityAudio(formData);
  },
);

export const saveQuestionnaireAnswers = createAsyncThunk(
  'influencerRequest/saveQuestionnaireAnswers',
  async requestData => {
    return await InfluencerRequestService.saveQuestionnaireAnswers(requestData);
  },
);

export const saveWritingStyleAnswers = createAsyncThunk(
  'influencerRequest/saveWritingStyleAnswers',
  async requestData => {
    return await InfluencerRequestService.saveWritingStyleAnswers(requestData);
  },
);

export const getOrCreateIdVerificationToken = createAsyncThunk(
  'influencerRequest/getOrCreateIdVerificationToken',
  async () => {
    return await InfluencerRequestService.getOrCreateIdVerificationToken();
  },
);

export const getQuestionnaire = createAsyncThunk('influencerRequest/getQuestionnaire', async () => {
  return await InfluencerRequestService.getQuestionnaire();
});

export const getAlbumMediaTags = createAsyncThunk('influencerRequest/getAlbumMediaTags', async () => {
  return await InfluencerRequestService.getAlbumMediaTags();
});

export const updateOnboardingType = createAsyncThunk('influencerRequest/updateOnboardingType', async requestData => {
  return await InfluencerRequestService.updateOnboardingType(requestData);
});

export const updateSubscriptionPrice = createAsyncThunk(
  'influencerRequest/updateSubscriptionPrice',
  async requestData => {
    return await InfluencerRequestService.updateSubscriptionPrice(requestData);
  },
);

export const fetchInfluencerRequestAlbumMedia = createAsyncThunk(
  'influencerRequest/fetchInfluencerRequestAlbumMedia',
  async requestData => {
    const { kind, tag } = requestData;
    return await InfluencerRequestService.fetchInfluencerRequestAlbumMedia(tag, kind);
  },
);

export const deleteInfluencerRequestAlbumMedia = createAsyncThunk(
  'influencerRequest/deleteInfluencerRequestAlbumMedia',
  async requestData => {
    const { mediaId } = requestData;
    return await InfluencerRequestService.deleteInfluencerRequestAlbumMedia(mediaId);
  },
);

export const setMediaPricing = createAsyncThunk('influencerRequest/setMediaPricing', async requestData => {
  return await InfluencerRequestService.setMediaPricing(requestData);
});

export const testVoiceElevenLabs = createAsyncThunk('influencerRequest/testVoiceElevenLabs', async requestData => {
  const { stability, message } = requestData;
  return await InfluencerRequestService.testVoiceElevenLabs(message, stability);
});

export const saveVoiceElevenLabs = createAsyncThunk('influencerRequest/saveVoiceElevenLabs', async requestData => {
  const { stability } = requestData;
  return await InfluencerRequestService.saveVoiceElevenLabs(stability);
});

export const uploadAudioElevenLabs = createAsyncThunk('influencerRequest/uploadAudioElevenLabs', async requestData => {
  const { audio } = requestData;
  const formData = new FormData();
  formData.append('file', audio.file, audio.file.name);
  return await InfluencerRequestService.uploadAudioElevenLabs(formData);
});
